/**
 * The learner's moments over the town: home's top bar opens this sheet, which slides up over home's live town with
 * what used to sit under it (the drafts notice, the learning setup, the scenario exam, the search, the tabs and the
 * list). Home renders its body (`body`), so every rule and request stays in home-view.
 *
 * The sheet is a history entry on home (`/#moments`), so the phone's Back closes it, as do its close button and
 * Escape. Focus moves into it as it opens and back to what opened it as it closes. Under reduced motion it fades in
 * instead of sliding. It scrolls inside, never the page.
 */
import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';

import { closeHash, focusBack, hashOpen, openHash } from './home-hash';

const HASH = '#moments';
const isOpen = () => hashOpen(HASH);

/** Opens the sheet as a history entry on home, so Back closes it. */
export const openMoments = () => openHash(HASH, 'moments');

@customElement('moments-sheet')
export class MomentsSheet extends LitElement {
  /** What the sheet holds, rendered by home. */
  @property({ attribute: false }) body: TemplateResult | typeof nothing = nothing;
  @state() open = isOpen();
  private returnTo: HTMLElement | null = null;
  private wasOpen = false;

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('popstate', this.onPop);
    window.addEventListener('keydown', this.onKey);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('popstate', this.onPop);
    window.removeEventListener('keydown', this.onKey);
    document.documentElement.classList.remove('moments-open');
    this.wasOpen = false;
  }

  private onPop = () => { this.open = isOpen(); };

  private onKey = (e: KeyboardEvent) => {
    // The capture panel, when it is up over the sheet, closes first.
    if (e.key === 'Escape' && this.open && !document.documentElement.classList.contains('capture-open')) this.close();
  };

  /** Back to home: the entry the sheet pushed goes, or, opened straight from its URL, the URL loses its hash. */
  close = () => {
    this.open = false;
    closeHash('moments');
  };

  protected updated() {
    document.documentElement.classList.toggle('moments-open', this.open);
    if (this.open === this.wasOpen) return;
    this.wasOpen = this.open;
    if (this.open) {
      const at = document.activeElement;
      this.returnTo = at instanceof HTMLElement && at !== document.body ? at : null;
      this.querySelector<HTMLElement>('.moments-close')?.focus();
    } else {
      focusBack(this.returnTo);
      this.returnTo = null;
    }
  }

  render() {
    if (!this.open) return nothing;
    return html`
      <div class="moments-dim" aria-hidden="true" @click=${this.close}></div>
      <section class="moments-panel" role="dialog" aria-modal="true" aria-labelledby="moments-title">
        <div class="moments-head">
          <h2 id="moments-title">${t('home.moments')}</h2>
          <button class="btn ghost icon moments-close" @click=${this.close} aria-label=${t('tend.close')}>✕</button>
        </div>
        ${this.body}
      </section>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'moments-sheet': MomentsSheet }
}
